import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import {
  STATUS_KEYS, CHANNEL_KEYS, JOB_TYPE_KEYS, RATE_TYPE_KEYS,
  statusBadgeClass, statusLabelKey, channelLabelKey, jobTypeLabelKey, rateTypeLabelKey,
  formatDate,
} from "@/lib/constants";
import { X, Save, ExternalLink, Plus, Trash2, Pencil, FileText, Mail, ShieldCheck, Download, HelpCircle, Sparkles, Upload, Loader2, ClipboardEdit } from "lucide-react";
import { toast } from "sonner";
import MatchInsightCard from "@/components/MatchInsightCard";
import SimilarJobsStrip from "@/components/SimilarJobsStrip";

export default function JobDetailSheet({ jobId, onClose, onUpdated, onOpenJob, onOpenLetterEditor }) {
  const { t, lang } = useT();
  const [job, setJob] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [newInterviewDate, setNewInterviewDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState(null);
  // Clarification-flow modal state — captures the note the employer
  // asked BEFORE we PATCH the status, so the badge and note land
  // together (no half-state where status flipped but note is empty).
  const [clarifyDialog, setClarifyDialog] = useState(false);
  const [clarifyDraft, setClarifyDraft] = useState("");
  const [clarifySaving, setClarifySaving] = useState(false);
  // Collapsible for the status/date/url/gegevens block — bijlagen and
  // recruiter-forms stay open at all times because the user asked to
  // keep the document work-surface visible; the more static metadata
  // hides behind a toggle so the sheet reads tidier.
  const [actionsOpen, setActionsOpen] = useState(false);
  // Inline date editor — mirrors the pattern of `handleStatusQuickChange`.
  // Toggle reveals a native <input type="date"> pre-filled with the current
  // date_applied (yyyy-mm-dd slice). Immediate save on change, no separate
  // Save-button so it feels as light as the status badges next to it.
  const [dateEditing, setDateEditing] = useState(false);
  const [dateSaving, setDateSaving] = useState(false);

  const loadJob = useCallback(async (id) => {
    try {
      const [j, att] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/attachments`).catch(() => ({ data: null })),
      ]);
      setJob(j.data);
      setAttachments(att.data);
      setForm({
        ...j.data,
        rate_amount: j.data.rate_amount ?? "",
        rate_type: j.data.rate_type ?? "hourly",
        hours_per_week: j.data.hours_per_week ?? "",
      });
    } catch { toast.error(t("detailLoadFailed")); onClose?.(); }
  }, [t, onClose]);

  useEffect(() => {
    if (!jobId) { setJob(null); setEditing(false); return; }
    loadJob(jobId);
  }, [jobId, loadJob]);

  if (!jobId) return null;
  if (!job || !form) {
    return (
      <div>
        <div className="ed-sheet-overlay" onClick={onClose} />
        <div className="ed-sheet flex items-center justify-center">
          <p className="ed-eyebrow">{t("loading")}</p>
        </div>
      </div>
    );
  }

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.job_title) { toast.error(t("addRequired")); return; }
    if (form.channel === "recruiter" && !form.recruiter_name) { toast.error(t("addRecruiterRequired")); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rate_amount: form.rate_amount === "" || form.rate_amount === null ? null : parseFloat(form.rate_amount),
        rate_type: form.rate_amount === "" || form.rate_amount === null ? null : form.rate_type,
        hours_per_week: form.hours_per_week === "" || form.hours_per_week === null || form.hours_per_week === undefined ? null : parseInt(form.hours_per_week, 10),
      };
      const { data } = await api.patch(`/jobs/${job.id}`, payload);
      setJob(data);
      setForm({
        ...data,
        rate_amount: data.rate_amount ?? "",
        rate_type: data.rate_type ?? "hourly",
        hours_per_week: data.hours_per_week ?? "",
      });
      setEditing(false);
      toast.success(t("detailUpdated"));
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("detailUpdateFailed"));
    } finally { setSaving(false); }
  };

  const handleDateChange = async (newDate) => {
    // newDate is yyyy-mm-dd from <input type="date">. Empty = user cleared;
    // silently ignore instead of nullifying (date_applied is required by
    // the backend model). Close editor on success so the sheet stays tidy.
    if (!newDate) { setDateEditing(false); return; }
    // No-op when unchanged (compare on 10-char slice to normalize ISO vs date).
    if ((job.date_applied || "").slice(0, 10) === newDate) {
      setDateEditing(false);
      return;
    }
    setDateSaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, { date_applied: newDate });
      setJob(data);
      setForm({
        ...data,
        rate_amount: data.rate_amount ?? "",
        rate_type: data.rate_type ?? "hourly",
        hours_per_week: data.hours_per_week ?? "",
      });
      setDateEditing(false);
      toast.success(t("detailDateUpdated"));
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("detailDateFailed"));
    } finally { setDateSaving(false); }
  };

  const handleStatusQuickChange = async (newStatus) => {
    // Special path for the "Verduidelijking gevraagd" flow: pop a
    // dialog so the user can capture WHAT the employer asked before
    // we flip the status. This keeps the badge honest — the sidebar
    // note is always populated once the badge appears.
    if (newStatus === "clarification" && job.status !== "clarification") {
      setClarifyDraft(job.clarification_note || "");
      setClarifyDialog(true);
      return;
    }
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, { status: newStatus });
      setJob(data);
      setForm({
        ...data,
        rate_amount: data.rate_amount ?? "",
        rate_type: data.rate_type ?? "hourly",
        hours_per_week: data.hours_per_week ?? "",
      });
      toast.success(t("detailStatusToast", { status: t(statusLabelKey(newStatus)) }));
      onUpdated();
    } catch { toast.error(t("detailStatusFailed")); }
  };

  const submitClarification = async () => {
    // Commit both status + note in a single PATCH so the badge and the
    // amber callout appear together. If the user provided a linked
    // motivation letter, we then hand them off to the Match-Verbeter
    // Wizard auto-launched inside the LetterEditor so they can start
    // reworking their answer immediately.
    setClarifySaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, {
        status: "clarification",
        clarification_note: clarifyDraft.trim(),
      });
      setJob(data);
      setForm({
        ...data,
        rate_amount: data.rate_amount ?? "",
        rate_type: data.rate_type ?? "hourly",
        hours_per_week: data.hours_per_week ?? "",
      });
      setClarifyDialog(false);
      toast.success(t("detailClarifySaved"));
      onUpdated();
      // Auto-navigate to the linked letter's Match-Verbeter Wizard when
      // there is one. Otherwise keep the sheet open — the callout will
      // suggest generating a letter first.
      const letterId = attachments?.letter?.id;
      if (letterId && onOpenLetterEditor) {
        onClose?.();
        onOpenLetterEditor(letterId, { autoOpenImprove: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("detailStatusFailed"));
    } finally { setClarifySaving(false); }
  };

  const openLinkedLetterImprove = () => {
    // "Aan de slag met de verduidelijking" CTA on the amber callout —
    // reopens the wizard on the linked letter without touching status.
    const letterId = attachments?.letter?.id;
    if (!letterId) {
      toast.error(t("detailClarifyNoLetter"));
      return;
    }
    if (onOpenLetterEditor) {
      onClose?.();
      onOpenLetterEditor(letterId, { autoOpenImprove: true });
    }
  };

  const addInterviewDate = () => {
    if (!newInterviewDate || form.interview_dates.includes(newInterviewDate)) return;
    update("interview_dates", [...form.interview_dates, newInterviewDate].sort());
    setNewInterviewDate("");
  };
  const removeInterviewDate = (d) => update("interview_dates", form.interview_dates.filter((x) => x !== d));

  const badge = statusBadgeClass[job.status] || "badge-applied";
  const showRate = true;

  return (
    <div data-testid="job-detail-sheet">
      <div className="ed-sheet-overlay" onClick={onClose} />
      <div className="ed-sheet ed-sheet-max">
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-neutral-200 px-8 py-5 flex items-start justify-between z-10">
          <div className="max-w-[80%]">
            <p className="ed-eyebrow">{t("detailEyebrow")}</p>
            <h2 className="ed-h2 mt-1 leading-tight">{job.job_title}</h2>
            <p className="font-serif text-lg text-neutral-700 mt-1 italic">{job.company}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              data-testid="close-detail-sheet"
              onClick={onClose}
              className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-10">
          {attachments && (
            <AttachmentsPanel t={t} jobId={job.id} attachments={attachments} lang={lang} />
          )}
          <RecruiterFormsSection t={t} jobId={job.id} attachments={attachments} />
          {/* Everything below (status, date, quick-status buttons,
              vacancy url, and the full Gegevens grid rendered by
              ReadView) collapses behind a single toggle to keep the
              sheet tidy — bijlagen + recruiter-forms remain always
              visible since they're the working area. */}
          <div className="ed-card">
            {/* Split header: the eyebrow + toggle live on the LEFT and act
                as the collapse control. On the RIGHT we place a Bewerken
                button that opens the same edit form as before — it lives
                here because the ReadView below is exactly what it edits. */}
            <div className="w-full flex items-center justify-between gap-3 px-6 py-4">
              <button
                type="button"
                data-testid="job-sheet-details-toggle"
                onClick={() => setActionsOpen((v) => !v)}
                className="flex-1 flex items-center justify-between gap-3 -mx-6 px-6 py-2 hover:bg-neutral-50 transition-colors"
                aria-expanded={actionsOpen}
              >
                <span className="ed-eyebrow inline-flex items-center gap-2">
                  <FileText size={12} strokeWidth={1.5} /> {t("detailMoreDetails")}
                </span>
                <span className="text-xs font-mono text-neutral-500 inline-flex items-center gap-1">
                  {actionsOpen ? t("detailCollapse") : t("detailExpand")}
                  <span className={`transition-transform ${actionsOpen ? "rotate-180" : ""}`}>▾</span>
                </span>
              </button>
              {!editing && (
                <button
                  data-testid="edit-job-button"
                  onClick={(e) => { e.stopPropagation(); setActionsOpen(true); setEditing(true); }}
                  className="ed-btn ed-btn-outline shrink-0"
                >
                  <Pencil size={14} strokeWidth={1.5} /> {t("edit")}
                </button>
              )}
            </div>
            {actionsOpen && (
              <div className="px-6 pb-6 pt-2 space-y-8 border-t border-neutral-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <p className="ed-eyebrow">{t("detailCurrent")}</p>
                    <span className={`ed-badge ${badge} mt-2`}>{t(statusLabelKey(job.status))}</span>
                  </div>
                  <div>
                    <p className="ed-eyebrow">{t("detailDate")}</p>
                    {dateEditing ? (
                      <input
                        type="date"
                        data-testid="job-date-input"
                        autoFocus
                        disabled={dateSaving}
                        value={(job.date_applied || "").slice(0, 10)}
                        onChange={(e) => handleDateChange(e.target.value)}
                        onBlur={() => setDateEditing(false)}
                        className="ed-input mt-2 font-serif text-lg"
                      />
                    ) : (
                      <button
                        type="button"
                        data-testid="job-date-edit-btn"
                        onClick={() => setDateEditing(true)}
                        title={t("detailDateEdit")}
                        className="mt-2 inline-flex items-center gap-2 font-serif text-xl border-b border-transparent hover:border-neutral-400 transition-colors"
                      >
                        {formatDate(job.date_applied, lang)}
                        {dateSaving
                          ? <Loader2 size={13} strokeWidth={1.7} className="animate-spin text-neutral-500" />
                          : <Pencil size={13} strokeWidth={1.5} className="text-neutral-400" />}
                      </button>
                    )}
                  </div>
                  <div>
                    <p className="ed-eyebrow">{t("detailChangeStatus")}</p>
                    <div className="flex gap-2 flex-wrap mt-2">
                      {STATUS_KEYS.map((k) => (
                        <button
                          key={k}
                          data-testid={`quick-status-${k}`}
                          onClick={() => handleStatusQuickChange(k)}
                          className={`ed-badge ${job.status === k ? statusBadgeClass[k] : "border border-neutral-300 bg-white text-neutral-500"} hover:border-neutral-900 transition-colors`}
                        >
                          {t(statusLabelKey(k))}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {job.url && (
                  <a href={job.url} target="_blank" rel="noreferrer" data-testid="job-url-link"
                     className="inline-flex items-center gap-2 font-mono text-xs text-neutral-700 hover:text-neutral-900 break-all border-b border-neutral-300 hover:border-neutral-900 pb-0.5">
                    <ExternalLink size={12} strokeWidth={1.5} />
                    {job.url}
                  </a>
                )}
                {editing ? null : (
                  <ReadView t={t} lang={lang} job={job} onOpenJob={onOpenJob} />
                )}
              </div>
            )}
          </div>

          {job.status === "clarification" && (
            <section
              data-testid="job-clarification-callout"
              className="border-2 border-[#F5B478] bg-[#FEF6EC] p-5 flex gap-4 items-start"
            >
              <HelpCircle size={22} strokeWidth={1.5} className="text-[#7A3A00] shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-[#7A3A00]">
                    {t("detailClarifyEyebrow")}
                  </p>
                  {job.clarification_requested_at && (
                    <p className="font-mono text-[0.6rem] text-neutral-600 mt-1">
                      {t("detailClarifyAskedAt", {
                        when: formatDate(job.clarification_requested_at.slice(0, 10), lang),
                      })}
                    </p>
                  )}
                </div>
                {job.clarification_note ? (
                  <p
                    data-testid="job-clarification-note"
                    className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap font-serif italic"
                  >
                    "{job.clarification_note}"
                  </p>
                ) : (
                  <p className="text-sm text-neutral-500 italic">
                    {t("detailClarifyNoNote")}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    data-testid="job-clarification-open-wizard"
                    onClick={openLinkedLetterImprove}
                    className="ed-btn"
                    title={t("detailClarifyCtaHint")}
                  >
                    <Sparkles size={13} strokeWidth={1.75} /> {t("detailClarifyCta")}
                  </button>
                  <button
                    type="button"
                    data-testid="job-clarification-edit-note"
                    onClick={() => { setClarifyDraft(job.clarification_note || ""); setClarifyDialog(true); }}
                    className="ed-btn ed-btn-outline"
                  >
                    <Pencil size={12} strokeWidth={1.75} /> {t("detailClarifyEditNote")}
                  </button>
                </div>
              </div>
            </section>
          )}

          {editing ? (
            <EditForm
              t={t} form={form} update={update} showRate={showRate}
              newInterviewDate={newInterviewDate} setNewInterviewDate={setNewInterviewDate}
              addInterviewDate={addInterviewDate} removeInterviewDate={removeInterviewDate}
              onCancel={() => { setForm({ ...job, rate_amount: job.rate_amount ?? "", rate_type: job.rate_type ?? "hourly", hours_per_week: job.hours_per_week ?? "" }); setEditing(false); }}
              onSave={handleSave} saving={saving}
            />
          ) : (
            <ReadView t={t} lang={lang} job={job} onOpenJob={onOpenJob} />
          )}
        </div>
      </div>

      {clarifyDialog && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4"
          data-testid="job-clarification-dialog"
        >
          <div className="bg-[#F9F8F6] border border-neutral-900 max-w-lg w-full p-8 space-y-5">
            <div>
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 flex items-center gap-2">
                <HelpCircle size={12} strokeWidth={1.75} />
                {t("detailClarifyDialogEyebrow")}
              </p>
              <h3 className="ed-h2 mt-2 leading-tight">{t("detailClarifyDialogTitle")}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                {t("detailClarifyDialogBody")}
              </p>
            </div>
            <textarea
              data-testid="job-clarification-note-input"
              value={clarifyDraft}
              onChange={(e) => setClarifyDraft(e.target.value)}
              rows={4}
              maxLength={1200}
              placeholder={t("detailClarifyDialogPh")}
              className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-3 py-2 text-sm leading-relaxed resize-y bg-white"
              autoFocus
            />
            <p className="font-mono text-[0.6rem] text-neutral-500">
              {clarifyDraft.length}/1200 · {t("detailClarifyDialogHint")}
            </p>
            <div className="flex justify-end gap-2">
              <button
                data-testid="job-clarification-cancel"
                type="button"
                onClick={() => { setClarifyDialog(false); setClarifyDraft(""); }}
                className="ed-btn ed-btn-outline"
                disabled={clarifySaving}
              >
                {t("cancel")}
              </button>
              <button
                data-testid="job-clarification-submit"
                type="button"
                onClick={submitClarification}
                disabled={clarifySaving}
                className="ed-btn disabled:opacity-50"
              >
                <Save size={13} strokeWidth={1.75} />
                {clarifySaving ? t("saving") : t("detailClarifyDialogSubmit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="ed-bento">
      <p className="ed-eyebrow">{label}</p>
      <p className="font-serif text-lg mt-2 break-words leading-snug">{value || <span className="text-neutral-300">—</span>}</p>
    </div>
  );
}

/**
 * Compact top-of-sheet card that surfaces every artefact linked to this job:
 * - Most recent AI-generated CV variant (fork) → falls back to the raw
 *   job-CV upload when no letter/fork exists yet.
 * - Motivation letter for this vacancy.
 * - Requirements-validation PDF derived from that letter.
 *
 * Every "Download" click triggers a real file download via an authenticated
 * blob-fetch (so the browser saves the file instead of previewing it in a
 * new tab). We surface the last-updated date so the user knows they're
 * grabbing the freshest version — critical after a Match-Verbeter rewrite.
 */
function AttachmentsPanel({ t, jobId, attachments, lang }) {
  const [busyKey, setBusyKey] = useState(null);

  const cv = attachments.cv || {};
  const variant = attachments.resume_variant || {};
  const letter = attachments.letter || {};
  const validation = attachments.validation_pdf || {};

  const fmtWhen = (iso) => {
    if (!iso) return "";
    try {
      // Show date + time so users can distinguish between an older upload
      // and a fresh regeneration from the Match-Verbeter wizard (which can
      // fire within the same day). 24h notation both in nl and en.
      return new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return ""; }
  };

  // Actually download the file through the authenticated api client. Cookie
  // + Bearer both flow through; the blob is turned into a temp <a> click so
  // the browser saves it under the server-provided filename.
  const downloadBlob = useCallback(async (key, path, fallbackName) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      const resp = await api.get(path, { responseType: "blob" });
      // Try to extract filename from Content-Disposition; fall back to the
      // provided fallbackName if the header is missing/opaque.
      let filename = fallbackName || "download";
      const cd = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      if (cd) {
        const m = /filename\*?="?([^";]+)"?/i.exec(cd);
        if (m && m[1]) filename = decodeURIComponent(m[1].trim());
      }
      const blob = new Blob([resp.data], { type: resp.data?.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("jobAttDownloadFailed"));
    } finally {
      setBusyKey(null);
    }
  }, [busyKey, t]);

  // Prefer the AI-variant when present (Match-Verbeter output), fall back
  // to the raw upload. This mirrors what the letter's downloadable PDF
  // uses under the hood — so what the user sees here is what the recruiter
  // will receive.
  const cvItem = variant.present
    ? {
        key: "cv",
        Icon: FileText,
        label: t("jobAttCv"),
        present: true,
        subtitle: variant.name || "cv",
        hint: variant.updated_at ? `${t("jobAttUpdated")} ${fmtWhen(variant.updated_at)}` : "",
        // Passing `letter_id` lets the backend align the CV's professional
        // title with the vacancy on-the-fly (no need for Match-Verbeter to
        // have run first). Stored CV stays untouched.
        path: `/resumes/${variant.id}/pdf?lang=${lang || "nl"}${letter.id ? `&letter_id=${letter.id}` : ""}`,
        fallbackName: `${(variant.name || "cv").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`,
      }
    : cv.present
    ? {
        key: "cv",
        Icon: FileText,
        label: t("jobAttCv"),
        present: true,
        subtitle: cv.filename || "cv.pdf",
        hint: cv.updated_at ? `${t("jobAttUpdated")} ${fmtWhen(cv.updated_at)}` : "",
        path: `/jobs/${jobId}/cv/download?download=true`,
        fallbackName: cv.filename || "cv.pdf",
      }
    : {
        key: "cv",
        Icon: FileText,
        label: t("jobAttCv"),
        present: false,
        subtitle: t("jobAttCvNone"),
        hint: "",
        path: null,
      };

  const items = [
    cvItem,
    {
      key: "letter",
      Icon: Mail,
      label: t("jobAttLetter"),
      present: letter.present,
      subtitle: letter.present
        ? `${letter.job_title || "?"} — ${letter.company || "?"}`
        : t("jobAttLetterNone"),
      hint: letter.updated_at ? `${t("jobAttUpdated")} ${fmtWhen(letter.updated_at)}` : "",
      path: letter.present ? `/letters/${letter.id}/pdf?doc=letter` : null,
      fallbackName: letter.present ? `motivatiebrief-${(letter.company || "brief").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf` : null,
    },
    {
      key: "validation",
      Icon: ShieldCheck,
      label: t("jobAttValidation"),
      present: validation.present,
      subtitle: validation.present ? t("jobAttValidationReady") : t("jobAttValidationNone"),
      hint: letter.present && letter.updated_at ? `${t("jobAttUpdated")} ${fmtWhen(letter.updated_at)}` : "",
      path: validation.present ? `/letters/${validation.letter_id}/pdf?doc=validation` : null,
      fallbackName: validation.present ? `eisen-validatie-${(letter.company || "brief").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf` : null,
    },
  ];

  return (
    <section className="ed-card p-5" data-testid="job-attachments-panel">
      <p className="ed-eyebrow mb-3 inline-flex items-center gap-2">
        <FileText size={12} strokeWidth={1.5} /> {t("jobAttTitle")}
      </p>
      <ul className="divide-y divide-neutral-200 border border-neutral-200">
        {items.map(({ key, Icon, label, present, subtitle, hint, path, fallbackName }) => (
          <li key={key} data-testid={`job-att-${key}`}
              className="flex items-center gap-4 px-4 py-3">
            <Icon size={16} strokeWidth={1.5} className={present ? "text-neutral-900 shrink-0" : "text-neutral-300 shrink-0"} />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
              <p className={`font-serif text-sm mt-0.5 truncate ${present ? "text-neutral-900" : "text-neutral-400 italic"}`}>{subtitle}</p>
              {hint && present && (
                <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">{hint}</p>
              )}
            </div>
            {path && (
              <button
                type="button"
                data-testid={`job-att-${key}-download`}
                onClick={() => downloadBlob(key, path, fallbackName)}
                disabled={busyKey === key}
                className="ed-btn ed-btn-outline !px-3 !py-1.5 shrink-0 disabled:opacity-50"
                title={t("jobAttDownload")}
              >
                {busyKey === key ? (
                  <Loader2 size={12} strokeWidth={1.7} className="animate-spin" />
                ) : (
                  <Download size={12} strokeWidth={1.7} />
                )}
                {t("jobAttDownload")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Recruiter Forms sub-panel — lets the user upload a .docx recruiter
 * intake form for THIS sollicitatie, pick approved references to
 * reveal, AI-fills the blanks with letter + validation + reference
 * context, and immediately downloads the result. Filled forms show
 * up in the list below with a Download shortcut.
 */
function RecruiterFormsSection({ t, jobId, attachments }) {
  const [forms, setForms] = useState([]);
  const [refs, setRefs] = useState([]);
  const [selectedRefs, setSelectedRefs] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewToken, setPreviewToken] = useState("");
  const [previewFilename, setPreviewFilename] = useState("");
  const [previewFields, setPreviewFields] = useState([]);
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        api.get(`/jobs/${jobId}/recruiter-forms`).catch(() => ({ data: [] })),
        api.get(`/references`).catch(() => ({ data: [] })),
      ]);
      setForms(Array.isArray(f.data) ? f.data : []);
      const rlist = Array.isArray(r.data) ? r.data.filter((x) => x.status === "approved") : [];
      setRefs(rlist);
    } catch {}
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const openPicker = () => inputRef.current?.click();
  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.docx?$/i.test(f.name)) { toast.error(t("recFormsWrongType")); return; }
    setFile(f); setPickerOpen(true);
  };
  const startPreview = async () => {
    if (!file || analyzing) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const ids = Object.entries(selectedRefs).filter(([, v]) => v).map(([k]) => k);
      fd.append("reference_ids", ids.join(","));
      const { data } = await api.post(`/jobs/${jobId}/recruiter-forms/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fields = Array.isArray(data?.fields) ? data.fields : [];
      // Each preview field gets a working `final_answer` = suggested_answer initially.
      setPreviewFields(fields.map((f) => ({ ...f, final_answer: f.suggested_answer || "" })));
      setPreviewToken(data?.preview_token || "");
      setPreviewFilename(data?.filename || file.name);
      setPickerOpen(false);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("recFormsFail"));
    } finally { setAnalyzing(false); }
  };
  const applyPreview = async () => {
    if (!previewToken || applying) return;
    setApplying(true);
    try {
      const payload = {
        preview_token: previewToken,
        fields: previewFields.map((f) => ({
          id: f.id,
          final_answer: f.final_answer || "",
          // Only send meets_status when the field actually has one — keeps
          // the server payload lean and preserves the server-derived value
          // for fields the user never touched.
          ...(f.meets_status ? { meets_status: f.meets_status } : {}),
        })),
      };
      const { data } = await api.post(`/jobs/${jobId}/recruiter-forms/apply`, payload);
      toast.success(t("recFormsFilled").replace("{n}", data?.fills_count ?? 0));
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/recruiter-forms/${data.id}/download`, "_blank");
      setPreviewOpen(false); setPreviewToken(""); setPreviewFields([]);
      setFile(null); setSelectedRefs({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("recFormsFail"));
    } finally { setApplying(false); }
  };
  const updateField = (id, patch) => {
    setPreviewFields((list) => list.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };
  const cancelPreview = () => {
    if (applying) return;
    setPreviewOpen(false); setPreviewToken(""); setPreviewFields([]);
    setFile(null);
  };
  const remove = async (id) => {
    if (!window.confirm(t("recFormsDeleteConfirm"))) return;
    try { await api.delete(`/jobs/${jobId}/recruiter-forms/${id}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("recFormsFail")); }
  };

  return (
    <section className="ed-card p-5" data-testid="job-recruiter-forms">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="ed-eyebrow inline-flex items-center gap-2">
          <ClipboardEdit size={12} strokeWidth={1.5} /> {t("recFormsTitle")}
        </p>
        <button
          type="button"
          data-testid="job-recruiter-forms-upload-btn"
          onClick={openPicker}
          disabled={analyzing}
          className="ed-btn"
          title={t("recFormsUploadHint")}
        >
          <Upload size={12} strokeWidth={1.75} /> {t("recFormsUploadCta")}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".docx,.doc"
          className="hidden"
          data-testid="job-recruiter-forms-input"
          onChange={onFileChosen}
        />
      </div>
      {forms.length === 0 ? (
        <p className="text-xs text-neutral-500 italic">{t("recFormsEmpty")}</p>
      ) : (
        <ul className="divide-y divide-neutral-200 border border-neutral-200">
          {forms.map((f, i) => (
            <li key={f.id} data-testid={`job-recruiter-form-${i}`} className="flex items-center gap-4 px-4 py-3">
              <FileText size={16} strokeWidth={1.5} className="text-neutral-900 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-sm truncate">{f.filled_filename}</p>
                <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">
                  {t("recFormsRowMeta")
                    .replace("{n}", f.fills_count)
                    .replace("{when}", new Date(f.created_at).toLocaleDateString())}
                </p>
              </div>
              <a
                data-testid={`job-recruiter-form-${i}-download`}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/recruiter-forms/${f.id}/download`}
                target="_blank"
                rel="noreferrer"
                className="ed-btn ed-btn-outline !px-3 !py-1.5 shrink-0"
                title={t("jobAttOpen")}
              >
                <Download size={12} strokeWidth={1.7} /> {t("jobAttOpen")}
              </a>
              <button
                type="button"
                data-testid={`job-recruiter-form-${i}-delete`}
                onClick={() => remove(f.id)}
                className="p-2 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
                title={t("recFormsDelete")}
              >
                <Trash2 size={12} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {pickerOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="job-recruiter-forms-picker">
          <div className="bg-[#F9F8F6] border border-neutral-900 max-w-lg w-full p-6 space-y-5">
            <div>
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 inline-flex items-center gap-2">
                <ClipboardEdit size={12} strokeWidth={1.75} /> {t("recFormsPickerEyebrow")}
              </p>
              <h3 className="ed-h2 mt-1 leading-tight">{t("recFormsPickerTitle")}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{t("recFormsPickerBody")}</p>
            </div>
            <div className="border border-neutral-200 p-3 font-mono text-xs text-neutral-700 truncate">
              📄 {file?.name}
            </div>
            <ValidationPreview t={t} attachments={attachments} />
            {refs.length > 0 && (
              <div>
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 mb-2">
                  {t("recFormsPickRefs")} ({refs.length})
                </p>
                <div className="max-h-40 overflow-y-auto border border-neutral-200 divide-y divide-neutral-200">
                  {refs.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-100 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        data-testid={`job-recruiter-form-ref-${r.id}`}
                        checked={!!selectedRefs[r.id]}
                        onChange={(e) => setSelectedRefs((s) => ({ ...s, [r.id]: e.target.checked }))}
                      />
                      <span className="font-serif truncate">{r.name}</span>
                      <span className="font-mono text-[0.6rem] text-neutral-500 truncate">{r.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="job-recruiter-forms-cancel"
                onClick={() => { setPickerOpen(false); setFile(null); }}
                disabled={analyzing}
                className="ed-btn ed-btn-outline"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                data-testid="job-recruiter-forms-submit"
                onClick={startPreview}
                disabled={analyzing}
                className="ed-btn disabled:opacity-50"
              >
                {analyzing ? <Loader2 size={13} strokeWidth={1.75} className="animate-spin" /> : <Sparkles size={13} strokeWidth={1.75} />}
                {analyzing ? t("recFormsAnalyzing") : t("recFormsAnalyzeCta")}
              </button>
            </div>
          </div>
        </div>
      )}
      {previewOpen && (
        <RecruiterFormPreview
          t={t}
          filename={previewFilename}
          fields={previewFields}
          applying={applying}
          onUpdate={updateField}
          onApply={applyPreview}
          onCancel={cancelPreview}
        />
      )}
    </section>
  );


/**
 * Compact preview that shows the recruiter which eisen (validation items)
 * are ready to auto-fill matching questions in the uploaded form. Rendered
 * inside the "Formulier invullen" picker modal so the user knows exactly
 * what content will land in the form before the AI-analyze step runs.
 *
 * Titles are split from the "Titel — Body" wire format so the list stays
 * scannable; long titles truncate on their own row and expandable on hover.
 * Show max 8 titles + a "+ N meer" bumper to keep the modal readable.
 */
function ValidationPreview({ t, attachments }) {
  const [expanded, setExpanded] = useState(false);
  const items = attachments?.letter?.validation_items || [];
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div
        data-testid="job-recruiter-forms-val-preview-empty"
        className="border border-dashed border-neutral-300 p-3 text-xs text-neutral-500 font-serif italic"
      >
        {t("recFormsValPrevEmpty")}
      </div>
    );
  }
  const titles = items
    .map((raw) => {
      if (typeof raw !== "string") return null;
      // wire format is "Titel — Body" (also " – ", " -- ", ": ").
      for (const sep of [" — ", " – ", " -- ", ": "]) {
        const idx = raw.indexOf(sep);
        if (idx > 0) return { title: raw.slice(0, idx).trim(), body: raw.slice(idx + sep.length).trim() };
      }
      return { title: raw.trim().slice(0, 90), body: "" };
    })
    .filter(Boolean);
  const MAX = 8;
  const shown = expanded ? titles : titles.slice(0, MAX);
  const rest = titles.length - shown.length;

  return (
    <div data-testid="job-recruiter-forms-val-preview">
      <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 inline-flex items-center gap-2 mb-2">
        <ShieldCheck size={11} strokeWidth={1.5} />
        {t("recFormsValPrevTitle")} ({titles.length})
      </p>
      <p className="text-xs text-neutral-600 leading-relaxed mb-2">
        {t("recFormsValPrevBody")}
      </p>
      <ul className="border border-neutral-200 divide-y divide-neutral-200 max-h-52 overflow-y-auto bg-white">
        {shown.map((v, i) => (
          <li
            key={i}
            data-testid={`job-recruiter-forms-val-item-${i}`}
            className="px-3 py-2 flex items-start gap-2"
            title={v.body || v.title}
          >
            <span className="font-mono text-[0.6rem] text-neutral-400 shrink-0 mt-0.5">{i + 1}.</span>
            <span className="font-serif text-sm text-neutral-900 leading-snug min-w-0 break-words">{v.title}</span>
          </li>
        ))}
      </ul>
      {rest > 0 && !expanded && (
        <button
          type="button"
          data-testid="job-recruiter-forms-val-preview-more"
          onClick={() => setExpanded(true)}
          className="mt-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-600 hover:text-neutral-900 underline underline-offset-2"
        >
          {t("recFormsValPrevMore").replace("{n}", rest)}
        </button>
      )}
    </div>
  );
}


}


/**
 * Preview modal — user reviews every AI-detected field, edits inline,
 * picks alternatives, then confirms to write the docx. Fields are
 * grouped by source/confidence so obvious ones (name/email from CV)
 * stay compact while open-text fields (motivation/competence) get
 * bigger textareas.
 */
function RecruiterFormPreview({ t, filename, fields, applying, onUpdate, onApply, onCancel }) {
  const confBadge = (c) => {
    if (c === "high") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (c === "low") return "bg-rose-100 text-rose-800 border-rose-200";
    return "bg-amber-100 text-amber-800 border-amber-200";
  };
  const srcLabel = (s) => t(`recFormsSrc_${s}`) || s;
  const emptyCount = fields.filter((f) => !(f.final_answer || "").trim()).length;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="job-recruiter-forms-preview">
      <div className="bg-[#F9F8F6] border border-neutral-900 max-w-4xl w-full max-h-[92vh] flex flex-col">
        <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 inline-flex items-center gap-2">
              <ClipboardEdit size={12} strokeWidth={1.75} /> {t("recFormsPreviewEyebrow")}
            </p>
            <h3 className="ed-h2 mt-1 leading-tight truncate">{filename}</h3>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              {t("recFormsPreviewBody").replace("{n}", fields.length).replace("{empty}", emptyCount)}
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={applying}
            data-testid="job-recruiter-forms-preview-close"
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors shrink-0 disabled:opacity-50"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {fields.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">{t("recFormsPreviewNoFields")}</p>
          ) : (
            fields.map((f, i) => {
              const isLong = ["motivation", "question", "achievement", "freetext", "experience", "references", "education", "competence"].includes(f.field_type);
              return (
                <div
                  key={f.id}
                  data-testid={`job-recruiter-forms-preview-field-${i}`}
                  className="border border-neutral-200 bg-white p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      {f.question && f.question !== f.label && (
                        <p
                          data-testid={`job-recruiter-forms-preview-field-${i}-question`}
                          className="font-serif italic text-sm text-neutral-700 leading-snug mb-1.5 pl-2 border-l-2 border-neutral-300 whitespace-pre-wrap"
                        >
                          {f.question}
                        </p>
                      )}
                      <p className="font-serif text-base text-neutral-900 truncate">{f.label}</p>
                      <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">
                        {t("recFormsFieldType")}: {f.field_type}
                        {f.placeholder ? ` · ${t("recFormsPlaceholderLabel")}: ${f.placeholder}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      {/* Meets-status selector — visible only when the AI
                          inferred (or overrode) a checklist verdict. Three
                          compact pills. Clicking flips locally and gets
                          persisted in the apply-payload so the docx runs
                          underlines the chosen option. */}
                      {f.meets_status && (
                        <div
                          className="inline-flex border border-neutral-300 bg-white"
                          data-testid={`job-recruiter-forms-preview-field-${i}-meets`}
                          role="group"
                          aria-label={t("recFormsMeetsLabel")}
                        >
                          {["ja", "gedeeltelijk", "nee"].map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              data-testid={`job-recruiter-forms-preview-field-${i}-meets-${opt}`}
                              onClick={() => onUpdate(f.id, { meets_status: opt })}
                              className={
                                "font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 border-r last:border-r-0 border-neutral-300 transition-colors " +
                                (f.meets_status === opt
                                  ? "bg-neutral-900 text-white"
                                  : "text-neutral-600 hover:bg-neutral-100")
                              }
                              title={t(`recFormsMeets_${opt}`)}
                            >
                              {t(`recFormsMeets_${opt}`)}
                            </button>
                          ))}
                        </div>
                      )}
                      <span className={`ed-badge border ${confBadge(f.confidence)} text-[0.6rem]`}>
                        {t(`recFormsConf_${f.confidence}`) || f.confidence}
                      </span>
                      <span className="ed-badge border border-neutral-300 bg-neutral-50 text-neutral-700 text-[0.6rem]">
                        {srcLabel(f.source)}
                      </span>
                    </div>
                  </div>
                  {isLong ? (
                    <textarea
                      data-testid={`job-recruiter-forms-preview-field-${i}-input`}
                      value={f.final_answer || ""}
                      onChange={(e) => onUpdate(f.id, { final_answer: e.target.value })}
                      placeholder={t("recFormsFieldPlaceholder")}
                      className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-sm p-2 font-serif leading-relaxed min-h-[90px]"
                      rows={4}
                    />
                  ) : (
                    <input
                      data-testid={`job-recruiter-forms-preview-field-${i}-input`}
                      type="text"
                      value={f.final_answer || ""}
                      onChange={(e) => onUpdate(f.id, { final_answer: e.target.value })}
                      placeholder={t("recFormsFieldPlaceholder")}
                      className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-sm p-2 font-serif"
                    />
                  )}
                  {Array.isArray(f.alternatives) && f.alternatives.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 self-center">
                        {t("recFormsAltHint")}:
                      </span>
                      {f.alternatives.map((alt, ai) => (
                        <button
                          key={`${f.id}-alt-${ai}`}
                          type="button"
                          data-testid={`job-recruiter-forms-preview-field-${i}-alt-${ai}`}
                          onClick={() => onUpdate(f.id, { final_answer: alt })}
                          className="ed-badge border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors text-[0.65rem] max-w-full truncate"
                          title={alt}
                        >
                          {alt.length > 60 ? `${alt.slice(0, 57)}…` : alt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between gap-3">
          <p className="font-mono text-[0.6rem] text-neutral-500">
            {t("recFormsPreviewFooter").replace("{n}", fields.length).replace("{empty}", emptyCount)}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="job-recruiter-forms-preview-cancel"
              onClick={onCancel}
              disabled={applying}
              className="ed-btn ed-btn-outline"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              data-testid="job-recruiter-forms-preview-apply"
              onClick={onApply}
              disabled={applying || fields.length === 0}
              className="ed-btn disabled:opacity-50"
            >
              {applying ? <Loader2 size={13} strokeWidth={1.75} className="animate-spin" /> : <Download size={13} strokeWidth={1.75} />}
              {applying ? t("recFormsApplying") : t("recFormsApplyCta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}




function ReadView({ t, lang, job, onOpenJob }) {
  const channelLabel = t(channelLabelKey(job.channel));
  const jobTypeLabel = t(jobTypeLabelKey(job.job_type));
  const rateLabel = job.rate_amount
    ? `€ ${Number(job.rate_amount).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { minimumFractionDigits: 2 })} / ${t(rateTypeLabelKey(job.rate_type || "hourly"))}`
    : null;

  return (
    <>
      <section>
        <p className="ed-eyebrow mb-4">{t("detailMeta")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-neutral-200">
          <MetaCell label={t("detailMetaChannel")} value={job.channel === "recruiter" ? `${channelLabel} · ${job.recruiter_name || "—"}` : channelLabel} />
          <MetaCell label={t("detailMetaJobType")} value={jobTypeLabel} />
          <MetaCell label={t("detailMetaRate")} value={rateLabel} />
          <MetaCell label={t("detailMetaLocation")} value={job.location} />
          <MetaCell label={t("detailMetaSalary")} value={job.salary_range} />
          <MetaCell label={t("detailMetaContact")} value={job.contact_person} />
          <MetaCell label={t("detailMetaHours")} value={job.hours_per_week ? `${job.hours_per_week} h/w` : null} />
        </div>
      </section>

      <section>
        <p className="ed-eyebrow mb-4">{t("detailCalendar")}</p>
        <div className="ed-card p-6">
          {job.interview_dates && job.interview_dates.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {job.interview_dates.map((d) => (
                <span key={d} className="ed-badge bg-neutral-900 text-[#F9F8F6] border border-neutral-900">
                  {formatDate(d, lang)}
                </span>
              ))}
            </div>
          ) : (<p className="text-sm text-neutral-500 italic font-serif">{t("detailNoInterviews")}</p>)}
        </div>
      </section>

      <section>
        <p className="ed-eyebrow mb-4">{t("fuHistory")}</p>
        <div className="ed-card p-6">
          {job.followup_log && job.followup_log.length > 0 ? (
            <ol className="space-y-3" data-testid="detail-followup-log">
              {[...job.followup_log].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-baseline gap-2 border-b border-neutral-100 last:border-0 pb-2 last:pb-0">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] rounded-full border border-neutral-900 px-2.5 py-0.5">
                    {t(entry.method === "email" ? "fuMethodEmail" : entry.method === "phone" ? "fuMethodPhone" : "fuMethodNote")}
                    {entry.auto ? ` · ${t("fuAuto")}` : ""}
                  </span>
                  <span className="font-mono text-[0.65rem] text-neutral-500">
                    {new Date(entry.at).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {entry.note && <span className="font-serif text-sm text-neutral-700 w-full whitespace-pre-wrap">{entry.note}</span>}
                </li>
              ))}
            </ol>
          ) : (<p className="text-sm text-neutral-500 italic font-serif">{t("fuNoHistory")}</p>)}
        </div>
      </section>

      <section>
        <p className="ed-eyebrow mb-4">{t("detailNotes")}</p>
        <div className="ed-card p-6">
          {job.notes ? (
            <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap text-neutral-800">{job.notes}</p>
          ) : (<p className="text-sm text-neutral-500 italic font-serif">{t("detailNoNotes")}</p>)}
        </div>
      </section>

      <section>
        <p className="ed-eyebrow mb-4">{t("detailDescription")}</p>
        <div className="ed-card p-8">
          {job.description ? (
            <pre className="font-serif text-base leading-loose whitespace-pre-wrap max-h-[28rem] overflow-y-auto text-neutral-800">
              {job.description}
            </pre>
          ) : (<p className="text-sm text-neutral-500 italic font-serif">{t("detailNoDescription")}</p>)}
        </div>
      </section>

      {/* AI Match Insight — cached on the job doc after first read so opening
          a familiar vacancy costs zero LLM tokens. Only rendered when a
          description exists (empty vacancies have nothing to analyse). */}
      <MatchInsightCard jobId={job.id} hasDescription={!!(job.description || "").trim()} />

      {/* Insight Compare — other vacancies with similar match-scores so the
          user can jump between comparable opportunities without hopping
          back to the overview. Hides itself when there's no cached score
          or nothing in the ±window. */}
      <SimilarJobsStrip
        jobId={job.id}
        onOpen={(otherId) => onOpenJob && onOpenJob(otherId)}
      />
    </>
  );
}

function EditForm({ t, form, update, showRate, newInterviewDate, setNewInterviewDate, addInterviewDate, removeInterviewDate, onCancel, onSave, saving }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div><label className="ed-label">{t("addPosition")}</label><input className="ed-input" value={form.job_title} onChange={(e) => update("job_title", e.target.value)} data-testid="edit-job-title" /></div>
        <div><label className="ed-label">{t("addCompany")}</label><input className="ed-input" value={form.company} onChange={(e) => update("company", e.target.value)} /></div>
        <div><label className="ed-label">{t("detailUrlLabel")}</label><input className="ed-input" value={form.url || ""} onChange={(e) => update("url", e.target.value)} /></div>
        <div><label className="ed-label">{t("addDate")}</label><input type="date" className="ed-input" value={form.date_applied} onChange={(e) => update("date_applied", e.target.value)} /></div>
        <div>
          <label className="ed-label">{t("addChannel")}</label>
          <select className="ed-input" value={form.channel} onChange={(e) => update("channel", e.target.value)}>
            {CHANNEL_KEYS.map((k) => <option key={k} value={k}>{t(channelLabelKey(k))}</option>)}
          </select>
        </div>
        {form.channel === "recruiter" && (
          <div><label className="ed-label">{t("addRecruiterName")}</label><input className="ed-input" value={form.recruiter_name || ""} onChange={(e) => update("recruiter_name", e.target.value)} /></div>
        )}
        <div>
          <label className="ed-label">{t("addJobType")}</label>
          <select className="ed-input" value={form.job_type} onChange={(e) => update("job_type", e.target.value)}>
            {JOB_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(jobTypeLabelKey(k))}</option>)}
          </select>
        </div>
        {showRate && (
          <>
            <div><label className="ed-label">{t("addRate")}</label><input type="number" className="ed-input" value={form.rate_amount} onChange={(e) => update("rate_amount", e.target.value)} /></div>
            <div>
              <label className="ed-label">{t("addRateType")}</label>
              <select className="ed-input" value={form.rate_type} onChange={(e) => update("rate_type", e.target.value)}>
                {RATE_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(rateTypeLabelKey(k))}</option>)}
              </select>
            </div>
          </>
        )}
        <div><label className="ed-label">{t("addLocation")}</label><input className="ed-input" value={form.location || ""} onChange={(e) => update("location", e.target.value)} /></div>
        <div><label className="ed-label">{t("addSalary")}</label><input className="ed-input" value={form.salary_range || ""} onChange={(e) => update("salary_range", e.target.value)} /></div>
        <div className="sm:col-span-2"><label className="ed-label">{t("addContact")}</label><input className="ed-input" value={form.contact_person || ""} onChange={(e) => update("contact_person", e.target.value)} /></div>
        <div><label className="ed-label">{t("addHoursPerWeek")}</label><input type="number" min="0" max="168" step="1" className="ed-input" value={form.hours_per_week ?? ""} onChange={(e) => update("hours_per_week", e.target.value)} data-testid="edit-hours-per-week" /></div>
      </div>

      <div>
        <label className="ed-label">{t("addInterviewDates")}</label>
        <div className="flex gap-3 items-end">
          <input type="date" className="ed-input flex-1" value={newInterviewDate} onChange={(e) => setNewInterviewDate(e.target.value)} />
          <button type="button" className="ed-btn" onClick={addInterviewDate}><Plus size={14} strokeWidth={1.5} /> {t("add")}</button>
        </div>
        {form.interview_dates.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {form.interview_dates.map((d) => (
              <span key={d} className="ed-badge badge-applied inline-flex gap-2">
                {d}
                <button type="button" onClick={() => removeInterviewDate(d)} className="text-rose-700">
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="ed-label">{t("addNotesLabel")}</label>
        <textarea rows={3} className="ed-input with-bg" value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} />
      </div>

      <div>
        <label className="ed-label">{t("addDescription")}</label>
        <textarea rows={8} className="ed-input with-bg font-serif text-base leading-relaxed" value={form.description || ""} onChange={(e) => update("description", e.target.value)} />
      </div>

      <div className="flex gap-3 justify-end pt-6 pb-20 border-t border-neutral-200">
        <button type="button" onClick={onCancel} className="ed-btn ed-btn-outline">{t("cancel")}</button>
        <button type="button" data-testid="save-edit-button" onClick={onSave} disabled={saving} className="ed-btn">
          <Save size={14} strokeWidth={1.5} /> {saving ? t("saving") : t("save")}
        </button>
      </div>
    </div>
  );
}
